/* eslint-disable react/no-array-index-key */
/* eslint-disable jsdoc/require-param */
import React from "react";
import {
  Paper,
  Stack,
  Button,
  IconButton,
  Tooltip,
  Avatar,
  Box,
  Typography,
  Chip,
} from "@mui/material";
import {
  ArrowBack,
  Refresh,
  TrendingUp,
  SupervisorAccount,
} from "@material-ui/icons";

/**
 * The UserDetailHeader component displays the header for the user detail page.
 *
 * It takes the following props:
 * - dealer: The user object containing user details.
 * - history: The history object from react-router-dom.
 * - growthLeader: Whether the user is a growth leader.
 * - isLeader: Whether the user is a leader.
 * - userSuppliersState: The state of the user's suppliers as comma-separated string.
 * - userProductTypesState: The state of the user's product types as comma-separated string.
 * - onRefresh: The function to call when the refresh button is clicked.
 *
 * The component displays the user's name, status, growth leader/trainer status, and suppliers/product types.
 */
export default function UserDetailHeader({
  dealer,
  history,
  growthLeader,
  isLeader,
  userSuppliersState,
  userProductTypesState,
  onRefresh,
}) {
  const getStatusColor = status => {
    switch (status) {
      case 2:
        return "success";
      case 3:
        return "warning";
      case 4:
        return "error";
      default:
        return "default";
    }
  };

  const getStatusLabel = status => {
    switch (status) {
      case 2:
        return "Active";
      case 3:
        return "Inactive";
      case 4:
        return "Deleted";
      default:
        return "Unknown";
    }
  };

  // Helper function to split comma-separated strings into arrays
  const parseChipData = dataString => {
    if (!dataString || typeof dataString !== "string") return [];
    return dataString
      .split(",")
      .map(item => item.trim())
      .filter(item => item.length > 0);
  };

  const suppliers = parseChipData(userSuppliersState);
  const productTypes = parseChipData(userProductTypesState);

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        mb: 3,
        background:
          "linear-gradient(135deg, #1a365d 0%, #2a4a61 25%, #4a7c7e 50%, #f7931e 100%)",
        borderRadius: 3,
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        mb={2}
      >
        <Button
          startIcon={<ArrowBack />}
          onClick={() => history.goBack()}
          sx={{
            color: "white",
            "&:hover": { bgcolor: "rgba(255,255,255,0.1)" },
          }}
        >
          Back
        </Button>
        <Tooltip title="Refresh Data">
          <IconButton
            onClick={onRefresh}
            sx={{
              color: "white",
              "&:hover": { bgcolor: "rgba(255,255,255,0.1)" },
            }}
          >
            <Refresh />
          </IconButton>
        </Tooltip>
      </Stack>

      <Stack direction="row" alignItems="center" spacing={3}>
        <Avatar
          sx={{
            width: 80,
            height: 80,
            bgcolor: "rgba(255,255,255,0.2)",
            fontSize: "2rem",
          }}
        >
          {dealer.first_name?.[0]}
          {dealer.surname?.[0]}
        </Avatar>
        <Box sx={{ flexGrow: 1 }}>
          <Typography
            variant="h4"
            sx={{ color: "white", fontWeight: 600, mb: 1 }}
          >
            Z-{dealer.id}: {dealer.first_name}
            {dealer?.middle_name ? ` ${dealer.middle_name} ` : " "}
            {dealer.surname}
          </Typography>

          {/* Status and Role Chips */}
          <Stack direction="row" spacing={2} alignItems="center" mb={2}>
            <Chip
              label={getStatusLabel(dealer.refs_user_lifecycle_status_id)}
              color={getStatusColor(dealer.refs_user_lifecycle_status_id)}
              size="small"
            />
            {growthLeader && (
              <Chip
                icon={<TrendingUp />}
                label="Growth Leader"
                color="warning"
                size="small"
              />
            )}
            {isLeader && (
              <Chip
                icon={<SupervisorAccount />}
                label="Trainer"
                color="info"
                size="small"
              />
            )}
          </Stack>

          {/* Suppliers Chips */}
          {suppliers.length > 0 && (
            <Box mb={1}>
              <Typography
                variant="caption"
                sx={{
                  color: "rgba(255,255,255,0.8)",
                  mb: 0.5,
                  display: "block",
                }}
              >
                Suppliers:
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {suppliers.map((supplier, index) => (
                  <Chip
                    key={`supplier-${index}`}
                    label={supplier}
                    size="small"
                    variant="outlined"
                    sx={{
                      color: "white",
                      borderColor: "rgba(255,255,255,0.5)",
                      backgroundColor: "rgba(255,255,255,0.1)",
                      "&:hover": {
                        backgroundColor: "rgba(255,255,255,0.2)",
                      },
                    }}
                  />
                ))}
              </Stack>
            </Box>
          )}

          {/* Product Types Chips */}
          {productTypes.length > 0 && (
            <Box>
              <Typography
                variant="caption"
                sx={{
                  color: "rgba(255,255,255,0.8)",
                  mb: 0.5,
                  display: "block",
                }}
              >
                Product Types:
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {productTypes.map((productType, index) => (
                  <Chip
                    key={`product-type-${index}`}
                    label={productType}
                    size="small"
                    variant="outlined"
                    sx={{
                      color: "white",
                      borderColor: "rgba(255,255,255,0.5)",
                      backgroundColor: "rgba(255,255,255,0.15)",
                      "&:hover": {
                        backgroundColor: "rgba(255,255,255,0.25)",
                      },
                    }}
                  />
                ))}
              </Stack>
            </Box>
          )}
        </Box>
      </Stack>
    </Paper>
  );
}
